import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CommonPopUp from '../../../components/popup/common-popup';
import InfluencerProfileCard from '../../signup/influencer/influencer-profile-card';
import { BrandDataService } from '../../../core/services/brand-data.service';
import { LocalService, PublicService } from '../../../core/services';
import { MenuPropsSelect } from '../../../core/utility/style';
import * as gtUtil from '../../../core/utility';

const brandDataService: BrandDataService = new BrandDataService();
const publicService: PublicService = new PublicService();
const localService: LocalService = new LocalService();

interface Industry {
    industry_id: number;
    industry_name: string;
}

interface InstagramProfileData {
    name?: string;
    insta_username?: string;
    profile_picture_url?: string;
    biography?: string;
    followers_count?: number;
    follows?: number;
    total_media?: number;
}

interface Props {
    openModal: boolean;
    /** `added` is true when a brand was actually inserted, so the caller can refresh its list. */
    onClose: (added: boolean) => void;
    user_id: any;
}

/**
 * Brand Pulse :: "Add Brand" form.
 *
 * Verifies the Instagram handle first (so we don't burn a Bright Data call on a typo), then inserts
 * the row in sheet_brand_handles_data with priority = 1, bright_data_priority = 1 and
 * service_running = 'true'. From there the profile-fetch service takes over:
 *  - the bright_data_priority job (runs every 30 min) pulls the public profile + media,
 *  - the priority job pushes the brand to the front of the post-analytics / sentiment queue,
 *  - service_running = 'true' keeps it refreshing on the 1st/15th + 2nd/16th crons.
 */
const AddBrandPopup: React.FC<Props> = ({ openModal, onClose, user_id }) => {
    const [username, setUsername] = useState('');
    const [brandName, setBrandName] = useState('');
    const [industryId, setIndustryId] = useState<string>('');
    const [industryList, setIndustryList] = useState<Industry[]>([]);

    const [verified, setVerified] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [profileData, setProfileData] = useState<InstagramProfileData>();
    const [message, setMessage] = useState('');
    // set when the handle is already in sheet_brand_handles_data, so we can say so on Verify
    // instead of letting the user fill in the name + industry and fail on submit
    const [duplicate, setDuplicate] = useState<{ ref_id: number; name: string } | null>(null);
    const navigate = useNavigate();

    /** ─────────────────────────────
     *  ✅ Industry dropdown
     *  ───────────────────────────── */
    useEffect(() => {
        if (!openModal) return;

        const getIndustries = async () => {
            try {
                const res = await brandDataService.getBrandPulseIndustries();
                if (res?.status === gtUtil.Constants.SUCCESS) {
                    setIndustryList(res?.data ?? []);
                }
            } catch (err) {
                console.error('Brand Pulse industries :: ', err);
            }
        };

        getIndustries();
    }, [openModal]);

    const resetForm = useCallback(() => {
        setUsername('');
        setBrandName('');
        setIndustryId('');
        setVerified(false);
        setProfileData(undefined);
        setMessage('');
        setDuplicate(null);
    }, []);

    const handleClose = (added: boolean) => {
        resetForm();
        onClose(added);
    };

    /** ─────────────────────────────
     *  ✅ Verify the handle before adding it
     *  ───────────────────────────── */
    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // any edit invalidates the previous verification
        setUsername(e.target.value.trim().toLowerCase());
        setVerified(false);
        setProfileData(undefined);
        setMessage('');
        setDuplicate(null);
    };

    const verifyHandle = async (event: any) => {
        event.preventDefault();
        setMessage('');
        setDuplicate(null);

        if (!/^[a-zA-Z0-9._]{1,30}$/.test(username)) {
            setMessage('Please enter a valid Instagram username.');
            return;
        }

        try {
            setVerifyLoading(true);

            // Check our own table first. It is a cheap lookup and, when the brand is already
            // tracked, there is no point spending an Instagram public-profile call on it.
            const existing = await brandDataService.checkBrandPulseUsername(username);
            if (existing?.status === gtUtil.Constants.SUCCESS && existing?.data?.exists) {
                setDuplicate({ ref_id: existing.data.ref_id, name: existing.data.name });
                setVerified(false);
                return;
            }

            const response = await publicService.instagramVerify(username);
            if (response?.status === gtUtil.Constants.SUCCESS) {
                setProfileData(response?.data);
                setVerified(true);
                // prefill the brand name, it stays editable
                setBrandName(response?.data?.name || response?.data?.insta_username || username);
            } else {
                setVerified(false);
                setMessage('Please check the handle name.');
            }
        } catch (err) {
            setVerified(false);
            setMessage('Please check the handle name.');
        } finally {
            setVerifyLoading(false);
        }
    };

    /** ─────────────────────────────
     *  ✅ Submit
     *  ───────────────────────────── */
    const handleSubmit = async (event: any) => {
        event.preventDefault();
        setMessage('');

        if (!brandName.trim()) {
            setMessage('Please enter the brand name.');
            return;
        }
        if (!industryId) {
            setMessage('Please select an industry.');
            return;
        }

        try {
            setSubmitLoading(true);
            const response = await brandDataService.addBrandPulseBrand({
                user_id,
                name: brandName.trim(),
                username,
                industry_id: industryId,
            });

            if (response?.status === gtUtil.Constants.SUCCESS) {
                localService.toastify(response?.info || 'Brand has been added.', 'success', 3000);
                handleClose(true);
            } else {
                setMessage(response?.info || 'Could not add the brand, please try again.');
            }
        } catch (err) {
            setMessage('Could not add the brand, please try again.');
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <CommonPopUp
            modalTitle='Add Brand'
            openModal={openModal}
            onClose={() => handleClose(false)}
            className='modal-medium add-brand-pulse-popup'
        >
            <form onSubmit={handleSubmit}>
                <div className='d-flex mb-3 align-items-start'>
                    <TextField
                        className='w-100 input-common me-2'
                        size='small'
                        label="Brand's Instagram Username"
                        variant='outlined'
                        value={username}
                        onChange={handleUsernameChange}
                        disabled={verified}
                    />
                    <LoadingButton
                        className='btn btn-primary btn-mui'
                        variant='contained'
                        type='button'
                        loading={verifyLoading}
                        disabled={!username || verified}
                        onClick={verifyHandle}
                    >
                        Verify
                    </LoadingButton>
                </div>

                {duplicate && (
                    <div className='add-brand-duplicate mb-3'>
                        <strong className='d-block fs-14'>
                            {duplicate.name} is already added on Brand Pulse
                        </strong>
                        <span className='fs-14 d-block'>
                            @{username} is already being tracked, so there is nothing to add.
                        </span>
                        <button
                            type='button'
                            className='btn btn-outline-primary btn-mui mt-2'
                            onClick={() => {
                                handleClose(false);
                                navigate(`/brand/info/details/${duplicate.ref_id}/${username}`);
                            }}
                        >
                            View brand
                        </button>
                    </div>
                )}

                {verified && (
                    <>
                        <InfluencerProfileCard
                            loading={verifyLoading}
                            platform='instagram'
                            name={profileData?.name ?? ''}
                            username={profileData?.insta_username ?? ''}
                            profile_image={profileData?.profile_picture_url ?? ''}
                            description={profileData?.biography ?? ''}
                            subscriber={profileData?.followers_count ?? 0}
                            follows={profileData?.follows ?? 0}
                            total_media={profileData?.total_media ?? 0}
                        />

                        <TextField
                            className='w-100 input-common mb-3'
                            size='small'
                            label='Brand Name'
                            variant='outlined'
                            value={brandName}
                            inputProps={{ maxLength: 150 }}
                            onChange={(e) => setBrandName(e.target.value)}
                        />

                        <FormControl className='w-100 input-common mb-3' size='small'>
                            <InputLabel id='brand-pulse-industry-label'>Industry</InputLabel>
                            <Select
                                labelId='brand-pulse-industry-label'
                                id='brand-pulse-industry'
                                label='Industry'
                                value={industryId}
                                MenuProps={MenuPropsSelect}
                                onChange={(e) => setIndustryId(e.target.value as string)}
                            >
                                {industryList.map((industry: Industry) => (
                                    <MenuItem key={industry.industry_id} value={`${industry.industry_id}`}>
                                        {industry.industry_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}

                <div className='d-flex align-items-center gap-2'>
                    <LoadingButton
                        className='btn btn-primary btn-mui'
                        variant='contained'
                        type='submit'
                        loading={submitLoading}
                        disabled={!verified}
                    >
                        Add Brand
                    </LoadingButton>
                    <button
                        type='button'
                        className='btn btn-outline-primary btn-mui'
                        onClick={() => handleClose(false)}
                    >
                        Cancel
                    </button>
                </div>

                {message && <span className='fs-14 d-block mt-2 error'>{message}</span>}

                {verified && !duplicate && (
                    <span className='fs-14 d-block mt-2' style={{ color: '#787878' }}>
                        The Brand Pulse service picks the brand up on its own — profile and posts within
                        about 30 minutes, then post analytics and comment sentiment on the next scheduled
                        pass (2nd &amp; 16th of every month). After that it refreshes every 15 days.
                    </span>
                )}
            </form>
        </CommonPopUp>
    );
};

export default AddBrandPopup;
